import React, { useEffect, useMemo, useState } from 'react';
import { Box, Link, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SelectGroup from '../../../components/casino/SelectGroup';
import { useAppSelector } from '../../../store';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import { useTranslation } from 'react-i18next';
import CusInput from '../../../components/material-ui/CusInput';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import {
  accountStatusOptions,
  colorByUsersStatus,
  ENUM_CUS_EVENT,
  optionsAvailability,
  popupInfoByType
} from '../../../enums/enums';
import CusButton from '../../../components/material-ui/CusButton';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import useToast from '../../../hooks/useToast';
import TotalUser from './TotalUser';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import { useEventListener } from 'usehooks-ts';
import DatePreset from '../../../components/material-ui/DatePreset';
import { useSearchParams } from 'react-router';
import useBootstrapSize from '../../../hooks/useBootstrapSize';

const playersOptions = [
  { label: '——', value: 'skip' },
  { label: 'by Email', value: 'email' },
  { label: 'via Telegram', value: 'telegram' }
];

export default function UsersFilter({ setDefaultSelectGroupId, onFindClick }) {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const [searchParams] = useSearchParams();
  const { isMobile, isLessTablet } = useBootstrapSize();
  const chosenGroupId = useAppSelector((state) => state.site.chosenGroupId);
  // const searchGroupId = searchParams.get('groupId');
  const searchGroupId = chosenGroupId;

  const payload = useAppSelector((state) => state.users.list.payload);
  const defaultPayload = useAppSelector((state) => state.users.list.defaultPayload);
  const isLayout = useAppSelector((state) => state.customizer.isLayout);
  const mainGroupId = useAppSelector((state) => state.user.group.main.groupId);
  const currenciesAll = useAppSelector((state) => state.user.info.currency);

  const [hideFilter, setHideFilter] = useState(true);

  const currencyOptions = Object.keys(currenciesAll).map((el) => ({ value: el, label: el }));

  currencyOptions.unshift({ label: t('——'), value: 'skip' });

  const isBoxed = isLayout === 'boxed';

  const clearFilter = { groupId: mainGroupId, ...defaultPayload };
  const defaultFilter = payload || clearFilter;

  const [filter, setFilter] = useState(defaultFilter);
  // searchGroupId && !payload.groupId ? { ...defaultFilter, groupId: searchGroupId } : defaultFilter
  const startDate = filter.dateRegFrom;
  const endDate = filter.dateRegTo;
  const lastOnline = filter.onlineBefore;
  const wentOnline = filter.onlineAfter;
  const filterGroup = filter.groupId;
  const disabledDeposit = filter.hasDeposit !== 'yes';

  const gridSize = { xs: 12, md: 6 };
  const gridSize2 = { xs: 12, md: 6, xl: 4 };
  const gridSize2p5 = { xs: 12, md: 6, lg: 3, xl: 3 };
  const gridSize3 = { xs: 12, md: 6, lg: 4, xl: 3 };
  const gridSize3last = { xs: 12, md: 6, xl: 3 };
  const isVerySmall = isBoxed;

  const optionsUserType = Object.values(popupInfoByType).map((el) => ({
    label: (
      <Stack direction={'row'} gap={1} alignItems={'center'}>
        {<el.icon size={16} color={colorByUsersStatus[el.value]} />}{' '}
        <TypographyText>{t(el.text)}</TypographyText>
      </Stack>
    ),
    value: el.value
  }));

  const optionsType = useMemo(() => {
    return [{ label: '——', value: 'skip' }, ...optionsUserType];
  }, []);

  const onResetFilter = () => {
    const resetFilter = { ...clearFilter };
    setFilter(resetFilter);
  };

  useEffect(() => {
    if (filter.groupId) return;

    if (searchGroupId) {
      const newFilter = { ...defaultFilter, groupId: searchGroupId };
      setFilter(newFilter);
      setDefaultSelectGroupId(searchGroupId);
      onFindClick(newFilter);
      return;
    }

    if (!mainGroupId) return;
    setFilter({ ...defaultFilter, groupId: mainGroupId });
    setDefaultSelectGroupId(mainGroupId);
  }, [searchGroupId, mainGroupId]);

  useEventListener(ENUM_CUS_EVENT.handlerOnFindClick, () => onFindClick());

  return (
    <Stack gap={2}>
      <Grid container spacing={0.75}>
        <Grid size={12}>
          <Stack
            display={'flex'}
            direction={'row'}
            gap={1}
            alignItems={'center'}
            flexWrap={'wrap'}
            mb={1}>
            <SelectGroup
              title="Group"
              hideTooltip
              selectGroupId={filterGroup}
              setSelectGroupId={(val) => {
                setFilter((prev) => ({ ...prev, groupId: val }));
                setDefaultSelectGroupId(val);
              }}
            />
            <CusCheckbox
              label={'all subgroups'}
              checked={filter.useSubgroups}
              onChange={() => setFilter((prev) => ({ ...prev, useSubgroups: !prev.useSubgroups }))}
            />

            <Box sx={{ ml: 'auto' }}>
              <Link component={'button'} onClick={() => setHideFilter((prev) => !prev)}>
                {hideFilter ? t('Show filters') : t('Hide filters')}
              </Link>
            </Box>
          </Stack>
        </Grid>
        <Grid size={12} container rowSpacing={isLessTablet ? 2 : 0.5} columnSpacing={1}>
          <Grid size={gridSize}>
            <CusDateTimePicker
              label="Registration date, from"
              value={startDate}
              onChange={(val) => setFilter((prev) => ({ ...prev, dateRegFrom: val }))}
              formatChange="YYYY-MM-DD HH:mm:ss"
              clearable
            />
          </Grid>
          <Grid size={gridSize}>
            <CusDateTimePicker
              label="Registration date, until"
              value={endDate}
              onChange={(val) => setFilter((prev) => ({ ...prev, dateRegTo: val }))}
              formatChange="YYYY-MM-DD HH:mm:ss"
              errorText={
                startDate && endDate && new Date(endDate).getTime() <= new Date(startDate).getTime()
                  ? t('End date must be later than start date')
                  : ''
              }
              clearable
            />
          </Grid>
          <Grid size={12}>
            <DatePreset
              setStartDate={(val) => setFilter((prev) => ({ ...prev, dateRegFrom: val }))}
              setEndDate={(val) => setFilter((prev) => ({ ...prev, dateRegTo: val }))}
            />
          </Grid>
        </Grid>
        <ShowContainer condition={!hideFilter}>
          <Box px={2} py={1} my={1} sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
            <Grid container spacing={1}>
              <Grid size={gridSize2p5}>
                <CusLabelSelect
                  isVerySmall={isVerySmall}
                  label={'Made a deposit'}
                  value={filter.hasDeposit}
                  options={optionsAvailability}
                  onChange={(val) => setFilter((prev) => ({ ...prev, hasDeposit: val }))}
                />
              </Grid>

              <Grid size={gridSize2p5}>
                <CusLabelSelect
                  isVerySmall={isVerySmall}
                  label="Currency"
                  fullWidth
                  value={filter.depositCurrency}
                  onChange={(val) => {
                    if (val == 'skip') {
                      setFilter((prev) => ({
                        ...prev,
                        depositAmountFrom: 0,
                        depositAmountTo: 0,
                        depositCurrency: val
                      }));
                      return;
                    }
                    setFilter((prev) => ({ ...prev, depositCurrency: val }));
                  }}
                  disabled={disabledDeposit}
                  options={currencyOptions}
                />
              </Grid>
              <Grid size={gridSize2p5}>
                <CusInput
                  isVerySmall={isVerySmall}
                  label={'Deposit amount from'}
                  tooltipText={'0 - the amount is not used'}
                  type="number"
                  value={String(+filter.depositAmountFrom)}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, depositAmountFrom: Math.abs(e.target.value) }))
                  }
                  inputProps={{ min: 0 }}
                  disabled={filter.depositCurrency == 'skip' || disabledDeposit}
                />
              </Grid>
              <Grid size={gridSize2p5}>
                <CusInput
                  isVerySmall={isVerySmall}
                  label={'Deposit amount by'}
                  type="number"
                  tooltipText={'0 - the amount is not used'}
                  value={String(+filter.depositAmountTo)}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, depositAmountTo: Math.abs(e.target.value) }))
                  }
                  inputProps={{ min: 0 }}
                  disabled={filter.depositCurrency == 'skip' || disabledDeposit}
                />
              </Grid>
              <Grid size={gridSize} pt={1}>
                <CusDatePicker
                  isVerySmall={isVerySmall}
                  label={'Deposit start date'}
                  value={filter.depositDateFrom}
                  onChange={(val) => setFilter((prev) => ({ ...prev, depositDateFrom: val }))}
                  formatChange="YYYY-MM-DD"
                  clearable
                  disabled={disabledDeposit}
                />
              </Grid>
              <Grid size={gridSize} py={1}>
                <CusDatePicker
                  isVerySmall={isVerySmall}
                  label={'Deposit end date'}
                  value={filter.depositDateTo}
                  onChange={(val) => setFilter((prev) => ({ ...prev, depositDateTo: val }))}
                  formatChange="YYYY-MM-DD"
                  clearable
                  disabled={disabledDeposit}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid size={gridSize}>
            <CusDatePicker
              label="Went online after date"
              value={wentOnline}
              onChange={(val) => setFilter((prev) => ({ ...prev, onlineAfter: val }))}
              formatChange="YYYY-MM-DD"
              clearable
            />
          </Grid>
          <Grid size={gridSize}>
            <CusDatePicker
              label="Last online until"
              value={lastOnline}
              onChange={(val) => setFilter((prev) => ({ ...prev, onlineBefore: val }))}
              formatChange="YYYY-MM-DD"
              clearable
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusInput
              isVerySmall={isVerySmall}
              label={'Registered under refcode'}
              value={filter.refCode}
              onChange={(e) => setFilter((prev) => ({ ...prev, refCode: e.target.value || null }))}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'There are unread messages'}
              value={filter.hasUnreadMsg}
              onChange={(val) => setFilter((prev) => ({ ...prev, hasUnreadMsg: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Pressed any button in the bot'}
              value={filter.pressAnyKey}
              onChange={(val) => setFilter((prev) => ({ ...prev, pressAnyKey: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'There is a deposit block'}
              value={filter.isDepositLock}
              onChange={(val) => setFilter((prev) => ({ ...prev, isDepositLock: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Launched any game'}
              value={filter.runGame}
              onChange={(val) => setFilter((prev) => ({ ...prev, runGame: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize2}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Created an invoice for input'}
              value={filter.hasInputInvoice}
              onChange={(val) => setFilter((prev) => ({ ...prev, hasInputInvoice: val }))}
              options={optionsAvailability}
            />
          </Grid>

          <Grid size={gridSize3}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Player registration'}
              options={playersOptions}
              onChange={(val) => setFilter((prev) => ({ ...prev, regType: val }))}
              value={filter.regType}
            />
          </Grid>
          <Grid size={gridSize3}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Bot removed'}
              value={filter.botDeleted}
              onChange={(val) => setFilter((prev) => ({ ...prev, botDeleted: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize3}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Account status'}
              value={filter.userStatus}
              onChange={(val) => setFilter((prev) => ({ ...prev, userStatus: val }))}
              options={accountStatusOptions}
            />
          </Grid>
          <Grid size={gridSize3}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Phone number provided'}
              value={filter.hasPhoneNum}
              onChange={(val) => setFilter((prev) => ({ ...prev, hasPhoneNum: val }))}
              options={optionsAvailability}
            />
          </Grid>
          <Grid size={gridSize3}>
            <Box sx={{ backgroundColor: (theme) => theme.palette.grey[100] }} p={1}>
              <CusInput
                isVerySmall={isVerySmall}
                label={'Login'}
                value={filter.userLogin}
                disabled={filter.userId}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, userLogin: e.target.value || null }))
                }
              />
            </Box>
          </Grid>
          <Grid size={gridSize3}>
            <Box sx={{ backgroundColor: (theme) => theme.palette.grey[100] }} p={1}>
              <CusInput
                isVerySmall={isVerySmall}
                label={'User Id'}
                value={filter.userId}
                disabled={filter.userLogin}
                onChange={(e) => {
                  const value = +e.target.value || null;
                  if (!+value && value !== null) {
                    errorToast('enter number');
                    return;
                  }
                  setFilter((prev) => ({ ...prev, userId: value || null }));
                }}
              />
            </Box>
          </Grid>
          <Grid size={gridSize3last}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Type'}
              value={filter.userType}
              options={optionsType}
              onChange={(val) => setFilter((prev) => ({ ...prev, userType: val }))}
            />
          </Grid>
          <Grid size={gridSize3last}>
            <CusLabelSelect
              isVerySmall={isVerySmall}
              label={'Online status'}
              value={filter.onlineStatus}
              options={optionsAvailability}
              onChange={(val) => setFilter((prev) => ({ ...prev, onlineStatus: val }))}
            />
          </Grid>
        </ShowContainer>
      </Grid>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="flex-end">
        <CusButton
          text="Reset filters"
          variant="outlined"
          sx={{ px: 5 }}
          onClick={onResetFilter}
          fullWidth={isMobile}
        />
        <CusButton
          text="Find"
          variant="contained"
          sx={{ px: 5 }}
          onClick={() => onFindClick(filter)}
          fullWidth={isMobile}
        />
      </Stack>
      <TotalUser />
    </Stack>
  );
}
